import React, { useCallback, useState, useEffect } from 'react'
import { css } from '@emotion/core'
import { trackEvent } from '@fs/zion-analytics'
import {
  BillboardText,
  BillboardButton,
  Paragraph,
  colors,
  IconButton,
  Row,
  Separator,
  useOverlay,
  DialogOverlay,
  DialogOverlayActions,
  Button,
  DangerButton,
  H2,
  H3,
  FlowGrid,
  useContainerWidth,
  Subheading,
} from '@fs/zion-ui'
import { MenuClose } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import {
  // getDaysInMonth,
  getDaysInYear,
  getDayOfYear,
  isAfter,
  isBefore,
  // isFirstDayOfMonth,
  intervalToDuration,
  startOfToday,
} from '@fs/zion-locale/date-fns'
import { useTranslation } from 'react-i18next'
import { useCurrentTemple, useGetTempleDismissed } from '../../hooks/api'
import { saveTempleCountdownDismiss } from '../../services'
import CompositeTempleImage from './CompositeTempleImage'
import { usePWAMode } from '../../hooks/appRootHooks'

const countdownPartsCss = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 88px;
  border-radius: 10px;
  background-color: ${colors.blue02};
  position: relative;
`

/**
 * Calculates the 'days' portion of the countdown.
 *
 * The number of days in the countdown is always 0 on the first day of the month.
 * For the other days of the month, we want the value to be the number of days remaining
 * in the current month.  The 'days' property of the date-fns duration seems to give us
 * the number of days remaining in the final month, which is always 31 for December. So,
 * instead, we have to calculate how many days left in the current month.
 *
 * Example:
 * birthYear = 2011
 * now = February 28, 2022
 * templeEligibilityDate = January 1, 2023
 *
 * Using the 'days' prop from a date-fns duration object, the countdown would be:
 *   0      10      4
 * Years  Months  Days
 *
 * Calculating the days remaining in the current month, the countdown would be:
 *   0      10      1
 * Years  Months  Days
 */
// const getCountdownDays = (now) => (isFirstDayOfMonth(now) ? 0 : getDaysInMonth(now) - now.getDate() + 1) // get countdown days left in month
const getCountdownDays = (now) => {
  const days = getDaysInYear(now) - getDayOfYear(now) + 1
  return days === 365 ? 0 : days
} // get countdown days left in year

/**
 * Calculates the time remaining before the user can get a temple recommend.
 */
function calculateCountdown(templeEligibilityDateStr) {
  // sanity check date format to avoid breaking error
  if (!templeEligibilityDateStr || !templeEligibilityDateStr.match(/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/))
    return {} // if no eligibilityDate given, don't calculate anything
  const templeEligibilityDate = new Date(`${templeEligibilityDateStr}T00:00:00`)

  /** Today's date with time portion set to zero to ensure accurate countdown calculation. */
  const now = startOfToday()

  /** The countdown will be hidden four months after the user's temple eligibility date. */
  const fourMonthsAfter = new Date(`${templeEligibilityDate.getFullYear()}-04-30T00:00:00`)

  /** Get the countdown values. */
  const duration = intervalToDuration({ start: now, end: templeEligibilityDate })

  return {
    isFourMonthsAfter: isAfter(now, fourMonthsAfter),
    showCountdown: isBefore(now, templeEligibilityDate),
    countdown: {
      years: duration?.years,
      months: duration?.months,
      days: getCountdownDays(now),
    },
  }
}

/**
 * Displays the years, months, and days remaining before the user is eligible to get a temple recommend.
 */
function Countdown({ countdown, handleDismiss, showMonths = false }) {
  const [t] = useTranslation()
  const yearsString = t('date-time.quantity.years', '{count, plural, =0 {# Years} one {# Year} other {# Years}}', {
    count: countdown?.years,
  })
  const monthsString = t('date-time.quantity.months', '{count, plural, =0 {# Months} one {# Month} other {# Months}}', {
    count: countdown?.months,
  })
  const daysString = t('date-time.quantity.days', '{count, plural, =0 {# Days} one {# Day} other {# Days}}', {
    count: countdown?.days,
  })

  return (
    <div data-testid="countdown">
      <BillboardText primaryColor={colors.gray90} secondaryColor={colors.gray90}>
        <H2 size="H5">{t('temple.eligible.heading.when', 'When will I be old enough to get a temple recommend?')}</H2>
      </BillboardText>
      <Separator size="sm" />
      <div css={countdownPartsCss}>
        <DismissBtn handleDismiss={handleDismiss} />
        <Row alignX="center">
          {countdown?.years > 0 && (
            <H3 size="H4" centered supplementary data-testid="countdown-years">
              {yearsString}
            </H3>
          )}
          {/* Design as of Jan 2023 is to hide months remaining and show only days in year remaining */}
          {showMonths && countdown?.months > 0 && (
            <H3 size="H4" centered supplementary data-testid="countdown-months">
              {monthsString}
            </H3>
          )}
          {countdown?.days >= 0 && (
            <H3 size="H4" centered supplementary data-testid="countdown-days">
              {daysString}
            </H3>
          )}
        </Row>
      </div>
    </div>
  )
}

function DismissBtn({ handleDismiss }) {
  const confirmDismissOverlay = useOverlay()
  const [t] = useTranslation()
  return (
    <>
      <div style={{ position: 'absolute', insetInlineEnd: '-10px', top: '-10px' }}>
        <IconButton
          data-testid="countdownDismissButton"
          Icon={MenuClose}
          color="gray"
          size="xs"
          emphasis="lightHigh"
          title={t('common-ui:dismiss.action', 'Dismiss')}
          onClick={confirmDismissOverlay.open}
        />
      </div>
      <DialogOverlay
        {...confirmDismissOverlay}
        closeOnClickAway={false}
        size="lg"
        title={t('common-ui:dismiss.action', 'Dismiss')}
        footer={
          <DialogOverlayActions>
            <Button
              alt={t('common-ui:cancel.action', 'Cancel')}
              onClick={confirmDismissOverlay.close}
              linkName="TempleCountdown:CANCEL:CLICK"
            >
              {t('common-ui:cancel.action', 'Cancel')}
            </Button>
            <DangerButton
              data-testid="countdownDismissConfirmButton"
              alt={t('common-ui:remove.action', 'Remove')}
              type="submit"
              emphasis="high"
              onClick={() => {
                handleDismiss()
                confirmDismissOverlay.close()
                trackEvent({ name_event: 'dismiss countdown confirm' })
              }}
            >
              {t('common-ui:remove.action', 'Remove')}
            </DangerButton>
          </DialogOverlayActions>
        }
      >
        <Paragraph>
          {t(
            'temple.countdown.remove-prompt.content',
            'Are you sure you want to remove your Temple Recommend Countdown reminder? It will be permanently removed from this page.'
          )}
        </Paragraph>
      </DialogOverlay>
    </>
  )
}

/**
 * Displays a message to the user that they are now eligible to meet with their bishop get a temple recommend,
 * and provides a link to show them the temple recommend questions.
 */
function Eligible() {
  const [t] = useTranslation()
  return (
    <div data-testid="eligible">
      <BillboardText primaryColor={colors.gray90}>
        <H2 size="H5">
          {t('temple.eligible.heading.now', 'You are old enough to have a temple recommend!')}
          <Subheading>
            {t(
              'temple.eligible.instructions',
              'The next step is to have a temple recommend interview with a member of the Bishopric or Branch Presidency.'
            )}
          </Subheading>
        </H2>
      </BillboardText>
      <Separator size="xs" />
      <BillboardButton emphasis="high" color="gray" to="temple/temple-recommend">
        {t('temple.eligible.recommend-questions.button', 'Recommend Questions')}
      </BillboardButton>
    </div>
  )
}

const OrdinancesReady = () => {
  const isPWA = usePWAMode()
  const isExternal = !isPWA
  const [t] = useTranslation()
  const headingString = t('temple.ordinances-ready.heading', 'Find a family name to take to the Temple')
  const subheadingString = t(
    'temple.ordinances-ready.subheading',
    'Help connect others to Jesus Christ through sacred covenants.'
  )
  const ctaString = t('temple.ordinances-ready.call-to-action', 'Use Ordinances Ready')

  return (
    <div data-testid="ordinances-ready">
      <H2 size="H4">
        {headingString}
        <Subheading>{subheadingString}</Subheading>
      </H2>
      <Separator size="md" />
      <BillboardButton
        emphasis="high"
        color="gray"
        to="/temple/ordinances-ready"
        external={isExternal}
        target="_blank"
        linkName="TempleCountdown:ORDINANCES_READY:CLICK"
      >
        {ctaString}
      </BillboardButton>
    </div>
  )
}

/**
 * Displays the time remaining for the user to get a temple recommend.  Once their temple eligibility date is
 * reached, it displays a message that they can make an appointment to get a recommend and a link to see the
 * temple recommend questions.  This component will be hidden from the user four months after their temple
 * eligibility date.
 */
export default function TempleCountdown({ templeEligibleDate }) {
  const [templeCountdownDismissed, setTempleCountdownDismissed] = useState(false)
  const [mode, setMode] = useState('ordinances-ready')
  const atWidth = useContainerWidth()
  const user = useUser()
  const { data: templeData } = useCurrentTemple({ loading: user?.userLoading, signedIn: user?.signedIn })
  const { isFourMonthsAfter, showCountdown, countdown } = calculateCountdown(templeEligibleDate)
  const { data: templeDismissedData, query: templeDismissedQuery } = useGetTempleDismissed()

  useEffect(() => {
    if (templeDismissedQuery?.isSuccess) {
      setTempleCountdownDismissed(templeDismissedData.dismissed)
    }
  }, [templeDismissedData, templeDismissedQuery?.isSuccess])

  useEffect(() => {
    if (showCountdown && !isFourMonthsAfter && !templeCountdownDismissed && countdown) setMode('countdown')
    else if (!showCountdown && !isFourMonthsAfter && countdown) setMode('eligible')
    else setMode('ordinances-ready')
  }, [countdown, isFourMonthsAfter, showCountdown, templeCountdownDismissed])

  const countdownCss = (centered) => css`
    display: flex;
    flex-direction: row;
    justify-content: ${atWidth({ default: 'center', lg: 'start' })};
    align-items: center;
    height: 196px;
    & > div {
      max-width: 300px;
      text-align: ${centered ? 'center' : 'start'};
    }
  `

  // Dismiss button handler
  const handleDismissClick = useCallback(() => {
    /** Set the countdown dismissed value to true on the backend. */
    if (!templeCountdownDismissed) {
      saveTempleCountdownDismiss()
        .then(() => {
          setTempleCountdownDismissed(true)
        })
        .catch((error) => {
          throw error
        })
    }
  }, [templeCountdownDismissed])

  return (
    <>
      <Separator size="sm" />
      <FlowGrid columnCount={atWidth({ default: 1, lg: 2 })} itemHeightEstimate={196}>
        <CompositeTempleImage
          templeImage={templeData?.temple?.urls?.mediumUrl ?? templeData?.imageUrl} // Going with the default image was yielding some low res images.  Lets go with something that should be 640px wide
          centered={atWidth({ default: true, lg: false })}
        />
        <div css={countdownCss(atWidth({ default: true, lg: false }))}>
          {mode === 'countdown' && <Countdown countdown={countdown} handleDismiss={handleDismissClick} />}
          {mode === 'eligible' && <Eligible />}
          {mode === 'ordinances-ready' && <OrdinancesReady />}
        </div>
      </FlowGrid>
      <Separator size="md" />
    </>
  )
}
